import React, { useEffect, useLayoutEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../../supabaseClient';
import api from '../../api';
import usePlayerMeta, { normName, abbrevInjury } from './usePlayerMeta';
import PlayerProfile, { prefetchProfile } from './PlayerProfile';
import { flip, reveal } from './flip';
import { LINEUP_FORMATS, slotLabel, teamLogo, teamsByUser, TeamAvatar, NHL_COLORS, CapMeter, payrollOf, fmtCap } from './poolFx';

// "My lineup" for lineup-mode pools — same board as the Draft Room lineup.
// Drag a player onto an open spot (move) or onto another player (swap).
// Tap works too (phones): tap a player, then tap where he goes.
// A player whose game has started is locked — the slot he had at puck drop counts for that game.
// readOnly + ownerId: someone else's lineup (Standings) — same board, no moving, points shown.
// STATS MODE: hold a card for 1 second → the board stops moving players and shows that player's profile
// right under his line; tap another card to see his profile; hold a card 1 second again to go back.
export default function LineupEditor({ pool, userId, lang = 'en', ownerId, ownerName, readOnly = false, pointsById = null }) {
  const owner = ownerId || userId;
  const { injuryMap } = usePlayerMeta();   // injured players get the same OUT / DTD / IR tag as the draft
  const fr = lang === 'fr';
  const t = fr ? TR.fr : TR.en;
  const slots = LINEUP_FORMATS[pool.lineup_format] || [];
  const [allPicks, setAllPicks] = useState([]);   // my picks incl. my NHL team
  const [slotOf, setSlotOf] = useState({});       // player_id -> slot id
  const [today, setToday] = useState({});         // team abbrev -> today's game
  const [selected, setSelected] = useState(null); // tap-to-move
  const [dragId, setDragId] = useState(null);
  const [overKey, setOverKey] = useState(null);   // what's being dragged over
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // stats mode (hold a card 1 s): profileId = the player whose profile is open
  const [statsMode, setStatsMode] = useState(false);
  const [profileId, setProfileId] = useState(null);
  const [holdId, setHoldId] = useState(null);       // the card being held (shows the 1-second fill)
  const holdTimer = useRef(null);
  const holdStart = useRef(null);                   // where the finger / mouse went down
  const skipClick = useRef(false);                  // the click that ends a long press isn't a tap
  const boardRef = useRef(null);
  const [quick, setQuick] = useState(false);        // switching between profiles: short fade, both gaps move together
  const [caretX, setCaretX] = useState(null);       // where the profile's arrow points (under its player's card)
  // Every change goes through flip() (see flip.js): the layout changes once, then the lines SLIDE to their new
  // place — no height animated frame by frame (that was what stuttered).
  const openWrap = () => boardRef.current?.querySelector('.pp-wrap:not(.pp-ghost)') || null;
  // show another player's profile: same line = the box stays, its content changes;
  // another line = the old box is wiped away while the lines slide and the new one is uncovered
  const showProfile = (id) => {
    if (id === profileId) return;
    const sameLine = profileId && slotOf[profileId] === slotOf[id];
    flip(boardRef.current, () => { setQuick(true); setProfileId(id); }, { ghost: sameLine ? null : openWrap() });
  };
  const exitStats = () => flip(boardRef.current, () => { setStatsMode(false); setProfileId(null); }, { ghost: openWrap() });
  const enterStats = (id) => flip(boardRef.current, () => { setQuick(false); setStatsMode(true); setProfileId(id); });
  const revealOnce = (el) => { if (el && !el.dataset.shown) { el.dataset.shown = '1'; reveal(el); } };
  // stats mode on: the rest of the roster's profiles are fetched quietly, so every tap is instant
  useEffect(() => {
    if (!statsMode) return undefined;
    let alive = true;
    (async () => {
      const ids = allPicks.filter((x) => x.position !== 'T').map((x) => x.player_id);
      for (let i = 0; i < ids.length && alive; i += 3) await Promise.all(ids.slice(i, i + 3).map(prefetchProfile));
    })();
    return () => { alive = false; };
  }, [statsMode, allPicks]);
  // the arrow on top of the profile sits under the card of the player it shows
  useLayoutEffect(() => {
    const place = () => {
      const board = boardRef.current;
      const cardEl = board && profileId ? board.querySelector(`[data-pid="${profileId}"]`) : null;
      const wrap = board ? board.querySelector('.pp-wrap:not(.pp-ghost) .pp') : null;   // measured against the profile box itself
      if (!cardEl || !wrap) { setCaretX(null); return; }
      const c = cardEl.getBoundingClientRect(), w = wrap.getBoundingClientRect();
      setCaretX(Math.round(c.left + c.width / 2 - w.left));
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [profileId, statsMode, slotOf]);
  const cancelHold = () => { clearTimeout(holdTimer.current); holdTimer.current = null; setHoldId(null); };
  useEffect(() => () => clearTimeout(holdTimer.current), []);
  const startHold = (e, p) => {
    if (e.button != null && e.button !== 0) return;   // left button / finger only
    cancelHold();
    prefetchProfile(p.player_id);   // his numbers are ready before the profile opens
    holdStart.current = { x: e.clientX, y: e.clientY };
    setHoldId(p.player_id);
    holdTimer.current = setTimeout(() => {
      holdTimer.current = null; setHoldId(null);
      skipClick.current = true;
      setTimeout(() => { skipClick.current = false; }, 700);
      setSelected(null);
      if (statsMode) exitStats(); else enterStats(p.player_id);
    }, 1000);
  };
  // moving (a drag, a scroll) is not a hold
  const moveHold = (e) => {
    const st = holdStart.current;
    if (holdTimer.current && st && Math.hypot(e.clientX - st.x, e.clientY - st.y) > 8) cancelHold();
  };

  const load = useCallback(async () => {
    const [{ data: myPicks }, { data: lineup }] = await Promise.all([
      supabase.from('pool_picks').select('*')
        .eq('pool_id', pool.id).eq('user_id', owner),
      supabase.from('pool_lineups').select('player_id, slot').eq('pool_id', pool.id).eq('user_id', owner),
    ]);
    setAllPicks(myPicks || []);
    setSlotOf(Object.fromEntries((lineup || []).map((l) => [l.player_id, l.slot])));
  }, [pool.id, owner]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let alive = true;
    const pull = async () => {
      try { const d = await api.fetchNhlToday(); if (alive) setToday(d?.teams || {}); } catch (e) { /* no locks shown */ }
    };
    pull();
    const id = setInterval(pull, 60 * 1000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const picks = allPicks.filter((p) => p.position !== 'T');
  const hasCap = !!pool.cap_mode && pool.cap_amount != null;
  const payroll = payrollOf(picks);
  const overCap = hasCap && payroll > pool.cap_amount;
  const myTeam = teamsByUser(allPicks)[owner];
  const colors = myTeam && NHL_COLORS[myTeam.abbrev];
  const byId = (id) => picks.find((p) => p.player_id === id);
  // locked = his game is being played right now (a finished game is over: he can move again for the next one)
  const isLocked = (p) => !!today[p?.team]?.started && !today[p?.team]?.final;
  const inSlot = (slotId) => picks.filter((p) => slotOf[p.player_id] === slotId)
    .sort((a, b) => (a.pick_number ?? 999) - (b.pick_number ?? 999));

  const save = async (next) => {
    const prev = slotOf;
    setSlotOf(next);           // optimistic
    setSaving(true); setError('');
    const { data, error: err } = await supabase.rpc('set_lineup', { p_user_id: userId, p_pool_id: pool.id, p_slots: next });
    setSaving(false);
    if (err || !data?.success) { setSlotOf(prev); setError(err?.message || data?.error || t.errSave); }
  };

  // move `id` onto a player (swap) or into a slot (move)
  const moveTo = (id, target) => {
    const a = byId(id);
    if (readOnly || !a || isLocked(a) || saving) return;
    if (target.player) {
      const b = byId(target.player);
      if (!b || b.player_id === a.player_id || b.position !== a.position || isLocked(b)) return;
      save({ ...slotOf, [a.player_id]: slotOf[b.player_id], [b.player_id]: slotOf[a.player_id] });
    } else if (target.slot) {
      if (target.slot.pos !== a.position || slotOf[a.player_id] === target.slot.id) return;
      save({ ...slotOf, [a.player_id]: target.slot.id });
    }
  };

  // tap-to-move (phones)
  const tapPlayer = (p) => {
    if (readOnly || isLocked(p) || saving) return;
    if (!selected) { setSelected(p.player_id); return; }
    if (selected === p.player_id) { setSelected(null); return; }
    const a = byId(selected);
    if (!a || a.position !== p.position) { setSelected(p.player_id); return; }
    setSelected(null);
    moveTo(a.player_id, { player: p.player_id });
  };
  const tapOpen = (slot) => {
    if (readOnly || !selected) return;
    const id = selected;
    setSelected(null);
    moveTo(id, { slot });
  };

  // drag & drop (mouse)
  const moving = byId(dragId || selected);
  const canDrop = (target) => {
    if (!moving) return false;
    if (target.player) { const b = byId(target.player); return !!b && b.player_id !== moving.player_id && b.position === moving.position && !isLocked(b); }
    return target.slot.pos === moving.position;
  };
  const dropProps = (key, target) => ({
    onDragOver: (e) => { if (!readOnly && canDrop(target)) { e.preventDefault(); setOverKey(key); } },
    onDragLeave: () => setOverKey((k) => (k === key ? null : k)),
    onDrop: (e) => { e.preventDefault(); setOverKey(null); const id = e.dataTransfer.getData('text/plain') || dragId; setDragId(null); moveTo(id, target); },
  });

  const card = (p) => {
    const g = today[p.team];
    const locked = isLocked(p);
    const key = `p-${p.player_id}`;
    return (
      <div
        key={p.player_id}
        data-pid={p.player_id}
        className={`dr-slot filled lu-card ${readOnly ? 'ro' : ''} ${holdId === p.player_id ? 'holding' : ''} ${statsMode && profileId === p.player_id ? 'profiled' : ''} ${selected === p.player_id ? 'selected' : ''} ${locked ? 'locked' : ''} ${overKey === key ? 'drop-over' : ''} ${dragId === p.player_id ? 'dragging' : ''} ${moving && canDrop({ player: p.player_id }) ? 'can-drop' : ''}`}
        draggable={!readOnly && !locked && !saving && !statsMode}
        onPointerDown={(e) => startHold(e, p)}
        onPointerMove={moveHold}
        onPointerUp={cancelHold}
        onPointerLeave={cancelHold}
        onPointerEnter={() => { if (statsMode) prefetchProfile(p.player_id); }}
        onPointerCancel={cancelHold}
        onContextMenu={(e) => { if (holdId || statsMode) e.preventDefault(); }}
        onDragStart={(e) => { cancelHold(); e.dataTransfer.setData('text/plain', p.player_id); e.dataTransfer.effectAllowed = 'move'; setDragId(p.player_id); setSelected(null); }}
        onDragEnd={() => { setDragId(null); setOverKey(null); }}
        onClick={() => { if (skipClick.current) { skipClick.current = false; return; } if (statsMode) { showProfile(p.player_id); return; } tapPlayer(p); }}
        title={statsMode ? t.statsTap : readOnly ? t.holdHint : locked ? t.lockedHint : t.dragHint}
        {...dropProps(key, { player: p.player_id })}
      >
        <img className="dr-slot-logo" alt="" src={teamLogo(p.team)} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
        <span className="dr-slot-name">{p.player_name}</span>
        <span className="dr-slot-meta">
          <b className="dr-slot-pos">{p.position}</b>
          {injuryMap?.get(normName(p.player_name)) && <span className="dr-tag inj" title={injuryMap.get(normName(p.player_name))}>{abbrevInjury(injuryMap.get(normName(p.player_name)))}</span>}
          {/* his team's game is over = he's done for today: back to the normal card right away */}
          {g && !g.final ? <span className={`lu-game${locked ? ' live' : ''}`}>{g.home ? t.vs : '@'} {g.opponent} · {locked ? t.playing : timeOf(g.startTimeUTC, fr)}</span> : <>{p.team} · #{p.pick_number ?? '—'}</>}
        </span>
        {pointsById && <span className="lu-pts"><b>{pointsById[p.player_id] ?? 0}</b><small>{t.pts}</small></span>}
        {locked && !pointsById && <span className="lu-lock">{t.locked}</span>}
      </div>
    );
  };

  const openSpot = (slot, i) => {
    const key = `o-${slot.id}-${i}`;
    return (
      <div
        key={key}
        className={`dr-slot open lu-card ${overKey === key ? 'drop-over' : ''} ${moving && moving.position === slot.pos ? 'can-drop' : ''}`}
        onClick={() => tapOpen(slot)}
        {...dropProps(key, { slot })}
      >
        <span className="dr-slot-plus" aria-hidden="true">+</span>
        <span className="dr-slot-name">{t.open}</span>
        <span className="dr-slot-meta"><b className="dr-slot-pos">{slot.pos}</b></span>
      </div>
    );
  };

  const GROUPS = [['F', t.forwards, 3], ['D', t.defence, 2], ['G', t.goalies, 2]];

  return (
    <div className="dr lu-host">
      <div
        ref={boardRef}
        className={`dr-lineup lu-board ${myTeam ? 'has-team' : ''} ${statsMode ? 'stats-mode' : ''}`}
        style={colors ? { '--team-primary': colors[0], '--team-secondary': colors[1] } : undefined}
      >
        <div className="dr-lineup-head">
          <TeamAvatar team={myTeam} name={readOnly ? ownerName : t.title} size={46} />
          <span className="dr-lineup-who">
            <span className="dr-lineup-name">{readOnly ? t.theirLineup(ownerName || '—') : t.title}</span>
            <span className="dr-lineup-team">{myTeam?.name || ''}</span>
          </span>
          <span className="dr-lineup-count">{t.format[pool.lineup_format] || pool.lineup_format}</span>
        </div>

        {hasCap && (
          <div className="lu-cap">
            <CapMeter payroll={payroll} cap={pool.cap_amount} label={t.payroll} />
            {overCap && !readOnly && <div className="cap-warn">{pool.cap_mode === 'penalty' ? t.overPenalty(fmtCap(payroll - pool.cap_amount)) : t.overHard}</div>}
          </div>
        )}
        <div key={statsMode ? 'stats' : 'move'} className={`lu-hint ${statsMode ? 'stats' : ''}`}>
          {statsMode ? t.statsHint : readOnly ? t.holdHint : moving && !dragId ? t.movingHint(moving.player_name) : `${t.hint} ${t.holdHint}`}
        </div>
        {error && <div className="pool-form-error">{error}</div>}

        {GROUPS.map(([pos, label]) => {
          const gSlots = slots.filter((s) => s.pos === pos);
          if (!gSlots.length) return null;
          return (
            <section key={pos} className="dr-lineup-group">
              <h4 className="dr-card-title" data-flip>{label}</h4>
              <div className="dr-lineup-lines" style={{ '--per-line': pos === 'F' ? 3 : 2 }}>
                {gSlots.map((slot) => {
                  const players = inSlot(slot.id);
                  const open = Math.max(0, slot.cap - players.length);
                  return (
                    <div key={slot.id} data-flip className={`dr-line lu-line ${slot.mult === 0 ? 'extra' : ''} ${slot.mult === 2 ? 'double' : ''}`}>
                      <div className="lu-line-tag">
                        {slotLabel(slot.id, fr)} · {slot.mult === 0 ? t.noPoints : `×${overCap && pool.cap_mode === 'penalty' ? Math.min(slot.mult, 1) : slot.mult}`}
                      </div>
                      <div className="dr-line-slots">
                        {players.map(card)}
                        {Array.from({ length: open }).map((_, i) => openSpot(slot, i))}
                      </div>
                    </div>
                  );
                }).flatMap((line, i) => {
                  // stats mode: the profile opens right under the line its player is on
                  const slot = gSlots[i];
                  const shown = statsMode && profileId ? inSlot(slot.id).find((p) => p.player_id === profileId) : null;
                  return shown ? [line, (
                    <div key={`profile-${slot.id}`} ref={revealOnce} className="pp-wrap" style={caretX != null ? { '--caret-x': `${caretX}px` } : undefined}>
                      <PlayerProfile key={shown.player_id} pick={shown} lang={lang} quick={quick}
                        poolPoints={pointsById ? (pointsById[shown.player_id] ?? 0) : null}
                        slot={slotLabel(slot.id, fr)} game={today[shown.team] || null}
                        onClose={exitStats} />
                    </div>
                  )] : [line];
                })}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function timeOf(iso, fr) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString(fr ? 'fr-CA' : 'en-CA', { hour: 'numeric', minute: '2-digit' });
}

const TR = {
  en: {
    title: 'My lineup',
    hint: 'Drag a player onto an open spot or another player — or tap one, then tap where he goes. Only your lines score; Line 1 and Pair 1 count double.',
    movingHint: (n) => `Moving ${n} — tap an open spot or a player to swap.`,
    dragHint: 'Drag to move · hold 1 s for his profile',
    holdHint: 'Hold a player’s card for 1 second to open his profile.',
    statsHint: 'Stats mode: tap a player to see his profile. Hold a card for 1 second to go back to moving players.',
    statsTap: 'Tap for his profile · hold 1 s to go back',
    forwards: 'Forwards', defence: 'Defence', goalies: 'Goalies',
    noPoints: '0 pts', open: 'Open spot', vs: 'vs', playing: 'playing', final: 'final',
    locked: 'LOCKED', lockedHint: 'His game has started — he stays in this spot for it.',
    errSave: 'Could not save your lineup',
    payroll: 'Payroll',
    theirLineup: (n) => `${n}'s lineup`, pts: 'pts',
    overPenalty: (by) => `You're ${by} over the cap: Line 1 and Pair 1 don't score double, and you only get 1 free-agent pickup this month.`,
    overHard: 'You are over the cap.',
    format: { small: 'Small', medium: 'Medium', large: 'Large', full: 'Full' },
  },
  fr: {
    title: 'Mon alignement',
    hint: 'Glisse un joueur sur une place libre ou sur un autre joueur — ou touche-le, puis touche où il va. Seuls tes trios marquent; le 1er trio et la 1re paire comptent double.',
    movingHint: (n) => `Tu déplaces ${n} — touche une place libre ou un joueur pour échanger.`,
    dragHint: 'Glisser pour déplacer · garde appuyé 1 s pour son profil',
    holdHint: 'Garde le doigt 1 seconde sur la carte d’un joueur pour ouvrir son profil.',
    statsHint: 'Mode stats : touche un joueur pour voir son profil. Garde une carte appuyée 1 seconde pour revenir au déplacement.',
    statsTap: 'Touche pour son profil · garde appuyé 1 s pour revenir',
    forwards: 'Attaquants', defence: 'Défenseurs', goalies: 'Gardiens',
    noPoints: '0 pt', open: 'Place libre', vs: 'vs', playing: 'en jeu', final: 'final',
    locked: 'VERROUILLÉ', lockedHint: 'Son match est commencé — il reste à cette place pour ce match.',
    errSave: 'Impossible d’enregistrer ton alignement',
    payroll: 'Masse salariale',
    theirLineup: (n) => `Alignement de ${n}`, pts: 'pts',
    overPenalty: (by) => `Tu dépasses le plafond de ${by} : le 1er trio et la 1re paire ne comptent pas double, et tu as 1 seule signature ce mois-ci.`,
    overHard: 'Tu dépasses le plafond.',
    format: { small: 'Petit', medium: 'Moyen', large: 'Grand', full: 'Complet' },
  },
};
